// Shared reCAPTCHA v3 lazy-injection helpers for embed bundles.
//
// Extracted from FormsEmbed (TD-221 Phase 4) so both forms and
// marketplace embeds share one implementation. Follows project
// rule #20: script injected ONLY on user action, never in index.html.

let recaptchaLoading: Promise<void> | null = null;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

/**
 * Lazily inject the reCAPTCHA v3 script tag. Idempotent — dedupes if
 * the script is already loaded or loading. Returns immediately (no
 * script load) when siteKey is empty/undefined, so keyless dev works.
 */
export function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (!siteKey) return Promise.resolve();
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (recaptchaLoading) return recaptchaLoading;

  recaptchaLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("reCAPTCHA script failed to load")), { once: true });
    document.head.appendChild(script);
  });

  return recaptchaLoading;
}

/**
 * Execute reCAPTCHA v3 and return the token string. Lazy-loads the
 * script first (idempotent). Returns "" when siteKey is empty so
 * keyless dev paths work without branching in the caller.
 */
export async function executeRecaptchaToken(siteKey: string, action: string): Promise<string> {
  if (!siteKey) return "";
  await loadRecaptchaScript(siteKey);
  if (!window.grecaptcha) return "";
  return new Promise<string>((resolve, reject) => {
    window.grecaptcha!.ready(() => {
      window.grecaptcha!.execute(siteKey, { action }).then(resolve).catch(reject);
    });
  });
}

/** Test-only: reset the memoized reCAPTCHA load promise. */
export function resetRecaptchaForTests(): void {
  recaptchaLoading = null;
}
